/**
 * Global error handling utility
 * Extracts meaningful error messages from various error response formats
 */

export interface ErrorResponse {
  status?: string;
  message?: string;
  messages?: string[];
  error?: string;
  errors?: string[];
  data?: any;
}

/**
 * Extracts a user-friendly error message from an error object
 * @param error - The error object from axios or other sources
 * @returns A user-friendly error message
 */
export function extractErrorMessage(error: any): string {
  // Default fallback message
  const defaultMessage = 'An unexpected error occurred. Please try again.';

  // Check if error has response data (axios error)
  if (error?.response?.data) {
    const data: ErrorResponse = error.response.data;

    // Check for expired session in any error message
    const checkForExpiredSession = (message: string): boolean => {
      const lowerMessage = message.toLowerCase();
      return lowerMessage.includes('token expired or invalid');
    };

    // Handle array of messages (most common format)
    if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
      const messages = data.messages.join(', ');
      if (checkForExpiredSession(messages)) {
        return 'Your application session has expired, please refresh or login to try again.';
      }
      return messages;
    }

    // Handle single message
    if (data.message) {
      if (checkForExpiredSession(data.message)) {
        return 'Your application session has expired, please refresh or login to try again.';
      }
      return data.message;
    }

    // Handle error field
    if (data.error) {
      if (checkForExpiredSession(data.error)) {
        return 'Your application session has expired, please refresh or login to try again.';
      }
      return data.error;
    }

    // Handle errors array
    if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      const errors = data.errors.join(', ');
      if (checkForExpiredSession(errors)) {
        return 'Your application session has expired, please refresh or login to try again.';
      }
      return errors;
    }

    // Handle nested data structure
    if (data.data?.message) {
      if (checkForExpiredSession(data.data.message)) {
        return 'Your application session has expired, please refresh or login to try again.';
      }
      return data.data.message;
    }

    if (data.data?.messages && Array.isArray(data.data.messages) && data.data.messages.length > 0) {
      const messages = data.data.messages.join(', ');
      if (checkForExpiredSession(messages)) {
        return 'Your application session has expired, please refresh or login to try again.';
      }
      return messages;
    }
  }

  // Handle network errors
  if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
    return 'Network error. Please check your connection and try again.';
  }

  // Handle timeout errors
  if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }

  // Handle specific HTTP status codes
  if (error?.response?.status) {
    const status = error.response.status;
    
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication required. Please log in again.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 422:
        return 'Invalid data provided. Please check your input.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again later.';
      default:
        return `Request failed with status ${status}. Please try again.`;
    }
  }

  // Fallback to error message if available
  if (error?.message) {
    const lowerMessage = error.message.toLowerCase();
    if (lowerMessage.includes('token expired or invalid')) {
      return 'Your application session has expired, please refresh or login to try again.';
    }
    return error.message;
  }

  return defaultMessage;
}

/**
 * Determines if an error is a network-related error
 * @param error - The error object
 * @returns True if it's a network error
 */
export function isNetworkError(error: any): boolean {
  return (
    error?.code === 'NETWORK_ERROR' ||
    error?.message?.includes('Network Error') ||
    error?.code === 'ECONNABORTED' ||
    !error?.response
  );
}

/**
 * Determines if an error is a server error (5xx)
 * @param error - The error object
 * @returns True if it's a server error
 */
export function isServerError(error: any): boolean {
  const status = error?.response?.status;
  return status >= 500 && status < 600;
}

/**
 * Determines if an error is a client error (4xx)
 * @param error - The error object
 * @returns True if it's a client error
 */
export function isClientError(error: any): boolean {
  const status = error?.response?.status;
  return status >= 400 && status < 500;
}
